import { TextField as MuiTextField, SelectChangeEvent } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Skeleton from '@mui/material/Skeleton';
import { Theme } from 'klayowebshared';
import React, { ChangeEvent, Component, FocusEvent } from 'react';
import RemoveIcon from '../resources/images/icons-remove.svg';

interface TextFieldProps<T = any> {
  id?: string;
  revalidate?: boolean;
  inputRef?: any;
  ref?: any;
  children?: React.ReactNode;
  label?: string | null;
  value?: T | null;
  readOnly?: boolean;
  defaultValue?: T | null;
  onClick?: (e: React.MouseEvent) => void;
  disabled?: boolean;
  autoComplete?: boolean;
  autoFocus?: boolean;
  rows?: number;
  loading?: boolean;
  className?: string;
  sx?: any;
  placeholder?: string;
  focusPlaceholder?: string;
  dense?: boolean;
  leadingIcon?: React.ReactNode;
  multiline?: boolean;
  helperText?: string | null;
  error?: boolean;
  endAdornment?: React.ReactNode;
  InputProps?: any;
  InputLabelProps?: any;
  fullWidth?: boolean;
  inputProps?: any;
  select?: boolean;
  SelectProps?: any;
  minRows?: number;
  maxRows?: number;
  validationText?: string;
  inputSx?: any;
  forceBlur?: boolean;
  clearable?: boolean;
  validationMethod?: (
    e?: FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>,
    value?: string
  ) => boolean;
  onChange?: (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<T>
  ) => void;
  onClear?: () => void;
  onFocus?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => void;
  type?: string;
  slotProps?: any;
  maxLength?: number;
}

interface TextFieldState {
  valid: boolean | null;
  focus: boolean;
  autofilled: boolean;
}

export class TextField<T = any> extends Component<TextFieldProps<T>, TextFieldState> {
  ref: React.RefObject<HTMLDivElement | null>;
  inputRef: any;

  constructor(props: any) {
    super(props);

    this.ref = React.createRef();

    this.state = {
      valid: null,
      focus: false,
      autofilled: false,
    };
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    if (this.props.forceBlur !== prevProps.forceBlur && this.props.forceBlur) this.forceBlur();
  }

  onAutoFillStart() {
    this.setState({ autofilled: true });
  }

  onAutoFillCancel() {
    this.setState({ autofilled: false });
  }

  forceBlur() {
    if (this.inputRef) this.inputRef.blur();
  }

  getInputRef() {
    const { inputProps, inputRef } = this.props;
    return inputProps && inputProps.ref && inputProps.ref.current
      ? inputProps.ref.current.node
      : inputRef
        ? inputRef
        : this.ref.current;
  }

  clearValue() {
    const { defaultValue, onClear } = this.props;

    const inputRef = this.getInputRef();
    if (!inputRef) return;

    if (!this.props.select) {
      inputRef.value = '';

      const e: any = new Event('input', {
        bubbles: true,
        cancelable: true,
        target: inputRef,
      } as any);

      inputRef.dispatchEvent(e);
    } else
      this.props.onChange?.({ target: { value: defaultValue } } as
        | ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        | SelectChangeEvent<T>);

    inputRef.focus();

    if (onClear) onClear();
  }

  onInput(e: any) {
    const { onChange, validationMethod } = this.props;
    const { valid } = this.state;

    const inputRef = this.getInputRef();

    if (inputRef && inputRef.setAttribute) inputRef.setAttribute('size', e.target.value.length);

    if (onChange) onChange(e);

    if (validationMethod) {
      const newValid = validationMethod(e, e.target.value);
      if (newValid !== valid) this.setState({ valid: newValid });
    }
  }

  onChange(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<T>) {
    const { onChange } = this.props;
    if (onChange) onChange(e);
  }

  onFocus(e: FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) {
    this.setState({ focus: true });
    const { onFocus } = this.props;
    if (onFocus) onFocus(e);
  }

  onBlur(e: FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) {
    const { onBlur, validationMethod } = this.props;
    const { valid } = this.state;

    if (validationMethod) {
      const newValid = validationMethod(e, e.target.value);
      if (newValid !== valid) this.setState({ valid: newValid, focus: false });
    } else this.setState({ focus: false });

    if (onBlur) onBlur(e);
  }

  onReValidate() {
    const { validationMethod } = this.props;
    const { valid } = this.state;

    if (validationMethod) {
      const newValid = validationMethod();
      if (newValid !== valid) this.setState({ valid: newValid, focus: false });
    }
  }

  setInputRef(inputRef: any) {
    if (inputRef) this.inputRef = inputRef;

    // Forward ref to parent component (e.g., Autocomplete needs this)
    const { inputRef: parentInputRef } = this.props;
    if (parentInputRef) {
      if (typeof parentInputRef === 'function') {
        parentInputRef(inputRef);
      } else if (parentInputRef && typeof parentInputRef === 'object') {
        parentInputRef.current = inputRef;
      }
    }
  }

  render() {
    const {
      id,
      ref,
      revalidate,
      inputRef,
      children,
      label,
      value,
      readOnly,
      defaultValue,
      onClick,
      disabled,
      autoComplete,
      autoFocus,
      rows,
      loading,
      className,
      sx,
      placeholder,
      focusPlaceholder,
      dense,
      leadingIcon,
      multiline,
      helperText,
      error,
      endAdornment,
      InputProps,
      InputLabelProps,
      fullWidth,
      inputProps,
      select,
      SelectProps,
      minRows,
      maxRows,
      validationText,
      inputSx,
      type,
      maxLength,
    } = this.props;
    const clearable =
      this.props.clearable !== false &&
      (select ? value !== defaultValue : value && (value as string).length !== 0);
    const { valid, focus, autofilled } = this.state;

    if (revalidate) this.onReValidate();

    // Note: We always render MuiTextField even when loading, because Autocomplete
    // needs an input element to exist. The loading skeleton is shown separately.
    if (loading && !this.props.InputProps) {
      // Only show skeleton when not used inside Autocomplete (which passes InputProps)
      return (
        <div className='klayo-textfield--loading'>
          <Skeleton width={70} height={20} />
          <Skeleton width={sx?.width || null} height={42} />
        </div>
      );
    }

    return (
      <MuiTextField
        id={id}
        ref={ref}
        inputRef={this.setInputRef.bind(this)}
        className={
          'klayo-textfield ' +
          (dense ? 'klayo-textfield-dense ' : '') +
          (label ? 'klayo-textfield-withlabel ' : '') +
          (className ? className : '') +
          (autofilled ? 'klayo-textfield--autofilled' : '')
        }
        label={label}
        variant='outlined'
        select={select}
        SelectProps={SelectProps}
        placeholder={focusPlaceholder && focus ? focusPlaceholder : placeholder}
        multiline={multiline}
        rows={rows}
        minRows={minRows}
        maxRows={maxRows}
        onInput={this.onInput.bind(this)}
        onChange={this.onChange.bind(this)}
        onClick={onClick}
        onFocus={this.onFocus.bind(this)}
        onBlur={this.onBlur.bind(this)}
        disabled={disabled}
        helperText={valid === false ? validationText : helperText}
        fullWidth={fullWidth}
        error={error || valid === false}
        InputLabelProps={{ shrink: true, variant: 'filled', ...InputLabelProps }}
        autoComplete={autoComplete === false ? 'off' : 'on'}
        autoFocus={autoFocus}
        value={value ? value : ''}
        inputProps={{ ref: inputRef ? inputRef : this.ref, ...inputProps }}
        InputProps={{
          notched: false,
          readOnly: readOnly,
          type: type,
          maxLength: maxLength,
          startAdornment: leadingIcon ? (
            <InputAdornment position='start'>{leadingIcon}</InputAdornment>
          ) : null,
          endAdornment: endAdornment ? (
            endAdornment
          ) : (
            <InputAdornment position='end'>
              <IconButton
                className='klayo-textfield_clearbutton'
                sx={{ display: clearable ? 'flex' : 'none' }}
                onClick={this.clearValue.bind(this)}
              >
                <RemoveIcon />
              </IconButton>
            </InputAdornment>
          ),
          ...InputProps,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: Theme.getStyleVar('--neon-blue-nb-200'),
            },
            // "& fieldset legend span": {
            //   display: "none"
            // },
            '&:hover fieldset': {
              borderColor: Theme.getStyleVar('--neon-blue-nb-300'),
            },
            '&.Mui-focused fieldset': {
              borderWidth: '2px',
              borderColor: Theme.getStyleVar('--neon-blue-nb-500'),
            },

            '& .MuiInputAdornment-root': {
              maxHeight: '1.5em',
            },
          },
          ...sx,
        }}
      >
        {children}
      </MuiTextField>
    );
  }
}
